import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/receipts/components/ReceiptsToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { RootState } from '@/setup/redux/RootReducer';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { getModuleStatuses, StatusManageItem } from '@/modules/settings/redux/SettingsCRUD';
import { getSuppliers } from '@/modules/suppliers/redux/SupplierCRUD';
import axiosInstance from '@/setup/axios/axiosInstance';

import { getReceiptDetail, postReceipt, saveReceipt } from '../redux/ReceiptCRUD';
import { InventoryReceiptFormData, InventoryReceiptRow, ReceiptLine } from '../types';
import ReceiptForm from './ReceiptForm';
import ReceiptList, { ReceiptListHandle } from './ReceiptList';

const buildFormMetadata = (formData: InventoryReceiptFormData, receiptId?: string) => ({ formData, receiptId });
const isUuid = (value?: string) => Boolean(value) && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value as string);

const createDefaultReceiptForm = (companyId: string): InventoryReceiptFormData => ({
  receipt_no: '',
  receipt_type: 'direct',
  status: '',
  status_code: '',
  status_display: '',
  status_color: '',
  company_id: companyId,
  company_name: '',
  supplier_id: '',
  supplier_name: '',
  warehouse_id: '',
  warehouse_name: '',
  purchase_id: null,
  purchase_no: null,
  return_source_issue_id: null,
  return_source_issue_no: null,
  received_at: new Date().toISOString(),
  posted_at: null,
  posted_by: null,
  note: '',
  lines: [],
});

const resolveCompanyIdForQuery = (companyId?: string) => {
  if (!companyId || companyId === 'all' || companyId === 'global' || !isUuid(companyId)) {
    return '';
  }
  return companyId;
};

interface ApiPurchaseListItem {
  id: string;
  po_no?: string;
  code?: string;
}

interface ApiPurchaseListResponse {
  results?: ApiPurchaseListItem[];
}

interface ApiPurchaseDetailLine {
  id?: string;
  sort_order?: number;
  item_id?: string;
  item_code?: string;
  item_name?: string;
  uom_id?: string;
  uom_name?: string;
  remaining_qty?: string | number | null;
  ordered_qty?: string | number | null;
  unit_price?: string | number | null;
  note?: string;
}

interface ApiPurchaseDetail {
  id: string;
  po_no?: string;
  company_id?: string;
  company_name?: string;
  supplier_id?: string;
  supplier_name?: string;
  warehouse_id?: string;
  warehouse_name?: string;
  lines?: ApiPurchaseDetailLine[];
}

interface ApiIssueListItem {
  id: string;
  issue_no?: string;
  code?: string;
}

interface ApiIssueListResponse {
  results?: ApiIssueListItem[];
}

interface ApiIssueDetailLine {
  id?: string;
  sort_order?: number;
  item_id?: string;
  item_code?: string;
  item_name?: string;
  uom_id?: string;
  uom_name?: string;
  issued_qty?: string | number | null;
  unit_cost?: string | number | null;
  note?: string;
}

interface ApiIssueDetail {
  id: string;
  issue_no?: string;
  company_id?: string;
  company_name?: string;
  warehouse_id?: string;
  warehouse_name?: string;
  lines?: ApiIssueDetailLine[];
}

interface ApiSupplierListItem {
  id: string;
  supplier_id?: string;
  name?: string;
  display_name?: string;
  code?: string;
}

interface ApiSupplierListResponse {
  results?: ApiSupplierListItem[];
}

export default function ReceiptsModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const listRef = useRef<ReceiptListHandle>(null);

  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [receiptStatuses, setReceiptStatuses] = useState<StatusManageItem[]>([]);
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [issueSearch, setIssueSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [issueLoading, setIssueLoading] = useState(false);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [purchaseOptions, setPurchaseOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [issueOptions, setIssueOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [supplierOptions, setSupplierOptions] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ company: 'all', status: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const listScopeCompanyId = useMemo(
    () => resolveCompanyIdForQuery(activeCompanyFilter),
    [activeCompanyFilter],
  );

  const {
    canCreate: canCreateReceipt,
    canUpdate: canUpdateReceipt,
    canDelete: canDeleteReceipt,
    hasPermission,
    resolveTabPermissions,
    resolvePermissionsForCompanyId,
    canReadRecord,
  } = useModuleToolbarPermissions({
    resource: 'inventory.receipt',
    activeCompanyFilter,
    updateTabTypes: ['update-receipt'],
  });
  const canPostReceipt = hasPermission('post') || hasPermission('approve') || canUpdateReceipt;

  const statusFilterOptions = useMemo(
    () => [{ id: 'all', name: 'Tất cả' }, ...receiptStatuses.map((status) => ({ id: status.id || '', name: status.name }))],
    [receiptStatuses]
  );

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh sách nhập kho', type: 'list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateReceipt) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateReceipt]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_receipts_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateReceipt),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-receipt', 'update-receipt'],
    allowMultipleTabTypes: ['new-receipt', 'update-receipt'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = listScopeCompanyId || selectedCompanyId;
      const defaultStatusId = receiptStatuses.find((status) => status.is_default)?.id || '';
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `PN #${currentTabs.filter((tab) => tab.type === 'new-receipt').length + 1}`,
        type: 'new-receipt',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata({
            ...createDefaultReceiptForm(baseCompanyId),
            status: defaultStatusId,
          }),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryReceiptFormData | undefined;
  const activeReceiptId = activeTabData?.metadata?.receiptId as string | undefined;

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    getModuleStatuses('receipt').then(setReceiptStatuses).catch(() => {});
  }, []);

  const refreshReceiptList = useCallback(() => {
    listRef.current?.requestRefresh();
  }, []);

  useEffect(() => {
    const loadPurchases = async () => {
      setPurchaseLoading(true);
      try {
        const query = new URLSearchParams();
        if (isUuid(listScopeCompanyId)) {
          query.append('company', listScopeCompanyId);
        }
        if (purchaseSearch) {
          query.append('search', purchaseSearch);
        }
        query.append('page_size', '50');
        const qs = query.toString();

        const response = await axiosInstance.get<ApiPurchaseListResponse | ApiPurchaseListItem[]>(`/inventory/purchases/${qs ? `?${qs}` : ''}`);
        const data = response.data;
        const rows = Array.isArray(data) ? data : (data.results || []);

        setPurchaseOptions(rows.map((entry) => ({
          id: entry.id,
          name: entry.po_no || entry.code || entry.id,
        })));
      } catch (_error) {
        setPurchaseOptions([]);
      } finally {
        setPurchaseLoading(false);
      }
    };

    void loadPurchases();
  }, [listScopeCompanyId, purchaseSearch]);

  useEffect(() => {
    const loadIssues = async () => {
      setIssueLoading(true);
      try {
        const query = new URLSearchParams();
        if (isUuid(listScopeCompanyId)) {
          query.append('company', listScopeCompanyId);
        }
        if (issueSearch) {
          query.append('search', issueSearch);
        }
        query.append('page_size', '50');
        const qs = query.toString();

        const response = await axiosInstance.get<ApiIssueListResponse | ApiIssueListItem[]>(`/inventory/issues/${qs ? `?${qs}` : ''}`);
        const data = response.data;
        const rows = Array.isArray(data) ? data : (data.results || []);

        setIssueOptions(rows.map((entry) => ({
          id: entry.id,
          name: entry.issue_no || entry.code || entry.id,
        })));
      } catch (_error) {
        setIssueOptions([]);
      } finally {
        setIssueLoading(false);
      }
    };

    void loadIssues();
  }, [listScopeCompanyId, issueSearch]);

  useEffect(() => {
    const loadSuppliers = async () => {
      setSupplierLoading(true);
      try {
        const response = await getSuppliers({
          search: supplierSearch.trim() || undefined,
          page_size: 50,
          ...(isUuid(listScopeCompanyId) ? { company_id: listScopeCompanyId } : {}),
        } as any) as ApiSupplierListResponse | ApiSupplierListItem[];

        const rows = Array.isArray(response) ? response : (response.results || []);
        setSupplierOptions(rows.map((entry) => ({
          // Some APIs return company-supplier mapping id + supplier_id.
          // Backend receipt serializer expects Supplier PK, so prefer supplier_id when available.
          id: entry.supplier_id || entry.id,
          name: entry.display_name || entry.name || entry.code || entry.supplier_id || entry.id,
          code: entry.code || '',
        })));
      } catch (_error) {
        setSupplierOptions([]);
      } finally {
        setSupplierLoading(false);
      }
    };

    void loadSuppliers();
  }, [listScopeCompanyId, supplierSearch]);

  const handleApplyBulkAction = async (action: 'approve' | 'delete', selectedRows: InventoryReceiptRow[]) => {
    if (!selectedRows.length) return;

    if (action === 'approve' && !canPostReceipt) {
      setSnackbarMessage({ message: 'Bạn không có quyền ghi sổ phiếu nhập.', severity: 'error' });
      return;
    }

    if (action === 'delete' && !canDeleteReceipt) {
      setSnackbarMessage({ message: 'Bạn không có quyền xóa phiếu nhập.', severity: 'error' });
      return;
    }

    setBulkActionLoading(true);
    try {
      const operations = selectedRows.map((row) => {
        if (action === 'delete') {
          return axiosInstance.delete(`/inventory/receipts/${row.id}/`);
        }
        return postReceipt(row.id);
      });

      const results = await Promise.allSettled(operations);
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failCount = results.length - successCount;

      if (failCount === 0) {
        setSnackbarMessage({
          message: action === 'delete' ? `Đã xóa ${successCount} phiếu nhập.` : `Đã duyệt ${successCount} phiếu nhập.`,
          severity: 'success',
        });
      } else {
        setSnackbarMessage({ message: `Thành công ${successCount}, thất bại ${failCount} phiếu.`, severity: 'error' });
      }

      refreshReceiptList();
    } catch (_error) {
      setSnackbarMessage({ message: 'Không thể thực hiện thao tác hàng loạt.', severity: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const updateFormData = (updates: Partial<InventoryReceiptFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activeReceiptId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeReceiptId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
  };

  const toNumber = (value: string | number | null | undefined): number => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const toReceiptLinesFromPurchase = (purchaseLines: ApiPurchaseDetailLine[]): ReceiptLine[] => {
    return (purchaseLines || []).map((line, index) => {
      const preferredQty = toNumber(line.remaining_qty ?? line.ordered_qty ?? 0);
      const qty = preferredQty > 0 ? preferredQty : 0;
      const unitCost = toNumber(line.unit_price);
      const lineAmount = qty * unitCost;

      return {
        id: `po-line-${line.id || index + 1}`,
        sort_order: line.sort_order ?? index + 1,
        item_id: line.item_id || '',
        item_code: line.item_code || '',
        item_name: line.item_name || '',
        uom_id: line.uom_id || '',
        uom_name: line.uom_name || '',
        received_qty: String(qty),
        accepted_qty: String(qty),
        rejected_qty: '0',
        unit_cost: String(unitCost),
        line_amount: String(lineAmount),
        purchase_line_id: line.id || null,
        lot_no: '',
        serial_numbers: '',
        note: line.note || '',
      };
    });
  };

  const handlePurchaseChange = async (
    tabId: number,
    receiptId: string | undefined,
    baseFormData: InventoryReceiptFormData,
    purchaseId: string,
    purchaseNo: string | null,
  ) => {
    const isUpdateMode = Boolean(receiptId);
    const tabPermissions = resolvePermissionsForCompanyId(baseFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    try {
      const response = await axiosInstance.get<ApiPurchaseDetail>(`/inventory/purchases/${purchaseId}/`);
      const purchase = response.data;
      const mappedLines = toReceiptLinesFromPurchase(purchase.lines || []);

      const nextFormData: InventoryReceiptFormData = {
        ...baseFormData,
        receipt_type: 'from_purchase',
        purchase_id: purchase.id || purchaseId,
        purchase_no: purchase.po_no || purchaseNo || '',
        company_id: purchase.company_id || baseFormData.company_id,
        company_name: purchase.company_name || baseFormData.company_name,
        supplier_id: purchase.supplier_id || '',
        supplier_name: purchase.supplier_name || '',
        warehouse_id: purchase.warehouse_id || baseFormData.warehouse_id,
        warehouse_name: purchase.warehouse_name || baseFormData.warehouse_name,
        lines: mappedLines,
      };

      updateTab(tabId, { metadata: buildFormMetadata(nextFormData, receiptId) });
      handleUnsavedChangesUpdate(true, tabId);
    } catch (_error) {
      setSnackbarMessage({ message: 'Không tải được chi tiết PO để lấy danh sách item.', severity: 'error' });
    }
  };

  const toReceiptLinesFromIssue = (issueLines: ApiIssueDetailLine[]): ReceiptLine[] => {
    return (issueLines || []).map((line, index) => {
      const qty = toNumber(line.issued_qty);
      const unitCost = toNumber(line.unit_cost);
      const lineAmount = qty * unitCost;

      return {
        id: `issue-line-${line.id || index + 1}`,
        sort_order: line.sort_order ?? index + 1,
        item_id: line.item_id || '',
        item_code: line.item_code || '',
        item_name: line.item_name || '',
        uom_id: line.uom_id || '',
        uom_name: line.uom_name || '',
        received_qty: String(qty),
        accepted_qty: String(qty),
        rejected_qty: '0',
        unit_cost: String(unitCost),
        line_amount: String(lineAmount),
        purchase_line_id: null,
        lot_no: '',
        serial_numbers: '',
        note: line.note || '',
      };
    });
  };

  const handleIssueChange = async (
    tabId: number,
    receiptId: string | undefined,
    baseFormData: InventoryReceiptFormData,
    issueId: string,
    issueNo: string | null,
  ) => {
    const isUpdateMode = Boolean(receiptId);
    const tabPermissions = resolvePermissionsForCompanyId(baseFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    try {
      const response = await axiosInstance.get<ApiIssueDetail>(`/inventory/issues/${issueId}/`);
      const issue = response.data;
      const mappedLines = toReceiptLinesFromIssue(issue.lines || []);

      const nextFormData: InventoryReceiptFormData = {
        ...baseFormData,
        receipt_type: 'return',
        return_source_issue_id: issue.id || issueId,
        return_source_issue_no: issue.issue_no || issueNo || '',
        purchase_id: null,
        purchase_no: null,
        supplier_id: '',
        supplier_name: '',
        company_id: issue.company_id || baseFormData.company_id,
        company_name: issue.company_name || baseFormData.company_name,
        warehouse_id: issue.warehouse_id || baseFormData.warehouse_id,
        warehouse_name: issue.warehouse_name || baseFormData.warehouse_name,
        lines: mappedLines,
      };

      updateTab(tabId, { metadata: buildFormMetadata(nextFormData, receiptId) });
      handleUnsavedChangesUpdate(true, tabId);
    } catch (_error) {
      setSnackbarMessage({ message: 'Không tải được chi tiết phiếu xuất để lấy danh sách item trả về.', severity: 'error' });
    }
  };

  const openExistingReceipt = (row: InventoryReceiptRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage({ message: 'Bạn không có quyền xem chi tiết phiếu nhập.', severity: 'error' });
      return;
    }
    guardAndRun(() => {
      const tabId = Date.now() + Math.floor(Math.random() * 1000);
      openTab({
        id: tabId,
        title: row.receipt_no || 'Phiếu nhập',
        type: 'update-receipt',
        closable: true,
        entityId: row.id,
        metadata: {
          companyId: row.company_id,
          ...buildFormMetadata({
          receipt_no: row.receipt_no,
          receipt_type: row.receipt_type,
          status: row.status,
          status_code: row.status_code,
          status_display: row.status_display,
          status_color: row.status_color,
          company_id: row.company_id,
          company_name: row.company_name,
          supplier_id: row.supplier_id,
          supplier_name: row.supplier_name,
          warehouse_id: row.warehouse_id,
          warehouse_name: row.warehouse_name,
          purchase_id: row.purchase_id,
          purchase_no: row.purchase_no,
          return_source_issue_id: null,
          return_source_issue_no: null,
          received_at: row.received_at,
          posted_at: null,
          posted_by: null,
          note: row.note || '',
          lines: [],
        }, row.id),
        },
      });

      void (async () => {
        try {
          const detail = await getReceiptDetail(row.id);
          updateTab(tabId, { metadata: buildFormMetadata(detail, row.id) });
        } catch (_error) {
          setSnackbarMessage({ message: 'Không tải được chi tiết phiếu nhập.', severity: 'error' });
        }
      })();
    });
  };

  const updateLineData = (lineId: string, updates: any) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activeReceiptId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextLines = (activeFormData.lines || []).map((line) => (line.id === lineId ? { ...line, ...updates } : line));
    const nextFormData = { ...activeFormData, lines: nextLines };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeReceiptId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };


  const handleSave = async () => {
    if (!activeFormData || !activeTabData?.id) return;

    const isUpdateMode = Boolean(activeReceiptId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật phiếu nhập.', severity: 'error' });
      return;
    }

    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Bạn không có quyền tạo phiếu nhập.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const response = await saveReceipt(activeFormData, activeReceiptId);
      const nextId = response.id || activeReceiptId;
      if (nextId) {
        const detail = await getReceiptDetail(nextId);
        updateTab(activeTabData.id, { title: detail.receipt_no || 'Phiếu nhập', entityId: nextId, metadata: buildFormMetadata(detail, nextId) });
      }

      handleUnsavedChangesUpdate(false, activeTabData.id);
      setSnackbarMessage({ message: 'Đã lưu phiếu nhập kho.', severity: 'success' });
      refreshReceiptList();
    } catch (_error) {
      setSnackbarMessage({ message: 'Lưu phiếu nhập thất bại.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePost = async () => {
    if (!activeFormData || !activeTabData?.id) {
      return;
    }

    if (!canPostReceipt) {
      setSnackbarMessage({ message: 'Bạn không có quyền ghi sổ phiếu nhập.', severity: 'error' });
      return;
    }

    setPosting(true);
    try {
      let nextReceiptId = activeReceiptId;
      const saved = await saveReceipt(activeFormData, nextReceiptId);
      nextReceiptId = saved.id || nextReceiptId;

      if (!nextReceiptId) {
        throw new Error('Receipt has not been saved yet.');
      }

      await postReceipt(nextReceiptId);
      const detail = await getReceiptDetail(nextReceiptId);
      updateTab(activeTabData.id, {
        title: detail.receipt_no || 'Phiếu nhập',
        entityId: nextReceiptId,
        metadata: buildFormMetadata(detail, nextReceiptId),
      });
      handleUnsavedChangesUpdate(false, activeTabData.id);
      setSnackbarMessage({ message: 'Ghi sổ phiếu nhập thành công.', severity: 'success' });
      refreshReceiptList();
    } catch (error: any) {
      const apiDetail = error?.response?.data?.detail;
      setSnackbarMessage({
        message: typeof apiDetail === 'string' && apiDetail ? apiDetail : 'Không thể ghi sổ phiếu nhập.',
        severity: 'error',
      });
    } finally {
      setPosting(false);
    }
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryReceiptFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);
              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              status: filters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={statusFilterOptions}
                          />
                        }
                        dataTable={
                          <ReceiptList
                            ref={listRef}
                            companyId={listScopeCompanyId || selectedCompanyId}
                            searchQuery={searchQuery}
                            status={filters.status}
                            onApplyBulkAction={handleApplyBulkAction}
                            bulkActionLoading={bulkActionLoading}
                            onSelectRow={openExistingReceipt}
                            canPost={canPostReceipt}
                            canDelete={canDeleteReceipt}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <ReceiptForm
                        formData={tabFormData}
                        receiptId={(tab.metadata?.receiptId as string | undefined) || undefined}
                        statusOptions={receiptStatuses}
                        purchaseOptions={purchaseOptions}
                        returnSourceOptions={issueOptions}
                        supplierOptions={supplierOptions}
                        purchaseLoading={purchaseLoading}
                        returnSourceLoading={issueLoading}
                        supplierLoading={supplierLoading}
                        saving={saving}
                        posting={posting}
                        canEdit={tab.type === 'new-receipt' ? tabPermissions.canCreate : tabPermissions.canUpdate}
                        canPost={canPostReceipt}
                        onPurchaseSearch={setPurchaseSearch}
                        onReturnSourceSearch={setIssueSearch}
                        onSupplierSearch={setSupplierSearch}
                        onChangeForm={(updates) => {
                          const tabReceiptId = tab.metadata?.receiptId as string | undefined;
                          const canEditTab = tab.type === 'new-receipt' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                          if (!canEditTab) {
                            return;
                          }
                          const applyUpdate = (nextFormData: InventoryReceiptFormData) => {
                            updateTab(tab.id, { metadata: buildFormMetadata(nextFormData, tabReceiptId) });
                            handleUnsavedChangesUpdate(true, tab.id);
                          };

                          const nextFormData = { ...tabFormData, ...updates };

                          if (Object.prototype.hasOwnProperty.call(updates, 'purchase_id')) {
                            const nextPurchaseId = updates.purchase_id || null;
                            const nextPurchaseNo = updates.purchase_no || null;

                            if (!nextPurchaseId) {
                              applyUpdate({
                                ...nextFormData,
                                purchase_id: null,
                                purchase_no: null,
                                supplier_id: '',
                                supplier_name: '',
                                lines: [],
                              });
                              return;
                            }

                            applyUpdate(nextFormData);
                            void handlePurchaseChange(tab.id, tabReceiptId, nextFormData, nextPurchaseId, nextPurchaseNo);
                            return;
                          }

                          if (Object.prototype.hasOwnProperty.call(updates, 'return_source_issue_id')) {
                            const nextIssueId = updates.return_source_issue_id || null;
                            const nextIssueNo = updates.return_source_issue_no || null;

                            if (!nextIssueId) {
                              applyUpdate({
                                ...nextFormData,
                                return_source_issue_id: null,
                                return_source_issue_no: null,
                                lines: [],
                              });
                              return;
                            }

                            applyUpdate(nextFormData);
                            void handleIssueChange(tab.id, tabReceiptId, nextFormData, nextIssueId, nextIssueNo);
                            return;
                          }

                          if (tab.id !== activeTabData?.id) {
                            applyUpdate(nextFormData);
                            return;
                          }

                          updateFormData(updates);
                        }}
                        onChangeLine={updateLineData}
                        onSave={handleSave}
                        onPost={handlePost}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert severity={snackbarMessage?.severity || 'success'} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
